import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const fmt = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

export default function DummyCartonUsageByPeriodDetail() {
  const navigate = useNavigate();

  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedPlant, setSelectedPlant] = useState("");

  const [summaryRows, setSummaryRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [meta, setMeta] = useState({});
  const [detailRows, setDetailRows] = useState([]);
  const [detailSummary, setDetailSummary] = useState({});

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      setLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/dummy-carton-usage-by-period",
          {},
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        const rows = data.rows || [];

        setSummaryRows(rows);
        setSummary(data.summary || {});
        setMeta(data);

        const periodRows = rows.filter(
          (row) => row.period_type === periodType
        );

        const periods = Array.from(
          new Set(periodRows.map((row) => row.period).filter(Boolean))
        ).sort().reverse();

        const plants = Array.from(
          new Set(
            periodRows
              .map((row) =>
                row.plant_id == null ? "" : String(row.plant_id)
              )
              .filter(Boolean)
          )
        ).sort((a, b) => Number(a) - Number(b));

        if (!selectedPeriod || !periods.includes(selectedPeriod)) {
          setSelectedPeriod(periods[0] || "");
        }

        if (!selectedPlant || !plants.includes(String(selectedPlant))) {
          setSelectedPlant(plants[0] || "");
        }
      } catch (e) {
        if (!active) return;

        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Dummy carton usage comparison could not be loaded."
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    loadSummary();

    return () => {
      active = false;
    };
  }, [periodType, selectedPeriod, selectedPlant]);

  const periodRows = useMemo(
    () => summaryRows.filter((row) => row.period_type === periodType),
    [summaryRows, periodType]
  );

  const periods = useMemo(
    () =>
      Array.from(
        new Set(periodRows.map((row) => row.period).filter(Boolean))
      ).sort().reverse(),
    [periodRows]
  );

  const plants = useMemo(() => {
    const ids = Array.from(
      new Set(
        periodRows
          .map((row) =>
            row.plant_id == null ? "" : String(row.plant_id)
          )
          .filter(Boolean)
      )
    ).sort((a, b) => Number(a) - Number(b));

    return ids.map((id) => {
      const row = periodRows.find(
        (item) => String(item.plant_id) === id
      );
      return {
        id,
        name: row?.plant || `Plant ${id}`,
      };
    });
  }, [periodRows]);

  const selectedAggregate = useMemo(
    () =>
      periodRows.find(
        (row) =>
          row.period === selectedPeriod &&
          String(row.plant_id) === String(selectedPlant)
      ),
    [periodRows, selectedPeriod, selectedPlant]
  );

  useEffect(() => {
    let active = true;

    async function loadDetail() {
      if (!selectedPeriod || !selectedPlant) {
        setDetailRows([]);
        setDetailSummary({});
        return;
      }

      setDetailLoading(true);
      setError("");

      try {
        const res = await api.post(
          "/ai/ceo-chat/dummy-carton-usage-by-period/detail",
          {
            period_type: periodType,
            period: selectedPeriod,
            plant_id: Number(selectedPlant),
          },
          { timeout: 120000 }
        );

        if (!active) return;

        const data = res.data || {};
        setDetailRows(data.rows || []);
        setDetailSummary(data.summary || {});
        setPage(1);
      } catch (e) {
        if (!active) return;

        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Dummy carton drill-down could not be loaded."
        );
        setDetailRows([]);
        setDetailSummary({});
      } finally {
        if (active) setDetailLoading(false);
      }
    }

    loadDetail();

    return () => {
      active = false;
    };
  }, [periodType, selectedPeriod, selectedPlant]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return detailRows;

    return detailRows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [detailRows, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, safePage, pageSize]);

  const exportCsv = () => {
    const cols = [
      ["ProductionDate", "Production Date"],
      ["PlantID", "Plant ID"],
      ["PackingTypeID", "Packing Type"],
      ["ActualMcs", "Actual MCS"],
    ];

    const esc = (value) => {
      const s = String(value ?? "");
      return /[",\n]/.test(s)
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const lines = [
      cols.map(([, label]) => esc(label)).join(","),
      ...filteredRows.map((row) =>
        cols.map(([key]) => esc(row?.[key])).join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dummy-carton-usage-${periodType}-${selectedPeriod || "all"}-plant-${selectedPlant || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cards = [
    {
      label: "Selected Dummy MCS",
      value: fmt.format(selectedAggregate?.dummy_mcs || 0),
      sub: `${selectedPeriod || "—"} · ${selectedAggregate?.plant || "—"}`,
    },
    {
      label: "Detail Records",
      value: fmt.format(detailSummary.records || 0),
      sub: "CsDailyProductionData records",
    },
    {
      label: "Total Dummy MCS",
      value: fmt.format(summary.total_dummy_mcs || 0),
      sub: "All validated dated + undated dummy MCS",
    },
    {
      label: "Reconciliation",
      value: meta?.reconciliation?.pass ? "PASS" : "PENDING",
      sub: meta?.validation?.status || "Validation status unavailable",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1700px] space-y-5">
        <div className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              R41 · CEO Dummy Carton Usage
            </div>

            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              Dummy Carton Usage by Plant — Week / Month
            </h1>

            <p className="mt-2 max-w-4xl text-sm text-slate-600">
              Validated ERP comparison of dummy carton usage by plant using
              CsDailyProductionData. Dummy usage is identified directly from
              PackingTypeID = DUMMY and measured as SUM(ActualMcs).
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/ceo-command-center?tab=process")}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Process
          </button>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
                Period Type
              </div>
              <div className="flex rounded-xl border border-slate-300 p-1">
                {[
                  ["month", "Month"],
                  ["week", "Week"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setPeriodType(value);
                      setSelectedPeriod("");
                      setPage(1);
                    }}
                    className="rounded-lg px-4 py-2 text-sm font-semibold"
                    style={{
                      background:
                        periodType === value ? "#0f62fe" : "transparent",
                      color:
                        periodType === value
                          ? "#fff"
                          : "var(--c-text-muted)",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Period
              </span>
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  setSelectedPeriod(e.target.value);
                  setPage(1);
                }}
                className="min-w-[150px] rounded-xl border border-slate-300 px-3 py-2"
              >
                {periods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm text-slate-600">
              <span className="mb-1 block text-xs font-semibold uppercase">
                Plant
              </span>
              <select
                value={selectedPlant}
                onChange={(e) => {
                  setSelectedPlant(e.target.value);
                  setPage(1);
                }}
                className="min-w-[180px] rounded-xl border border-slate-300 px-3 py-2"
              >
                {plants.map((plant) => (
                  <option key={plant.id} value={plant.id}>
                    {plant.name} · Plant {plant.id}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-3 text-xs text-slate-500">
            Date basis: ProductionDate · Weekly basis: ISO YEARWEEK · Source:
            CsDailyProductionData
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border bg-white p-5 shadow-sm"
            >
              <div className="text-sm font-medium text-slate-500">
                {card.label}
              </div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">
                {loading || detailLoading ? "…" : card.value}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {card.sub}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Plant Comparison — {periodType === "month" ? "Month" : "ISO Week"}
              </h2>
              <p className="text-sm text-slate-500">
                Dummy MCS by plant · selected period: {selectedPeriod || "—"}
              </p>
            </div>

            <div className="text-xs text-slate-500">
              Reconciliation:{" "}
              <span
                className={
                  meta?.reconciliation?.pass
                    ? "font-semibold text-emerald-700"
                    : "font-semibold text-red-700"
                }
              >
                {meta?.reconciliation?.pass ? "PASS" : "PENDING"}
              </span>
            </div>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3">Plant</th>
                  <th className="px-3 py-3">Period</th>
                  <th className="px-3 py-3 text-right">Dummy MCS</th>
                  <th className="px-3 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {periodRows
                  .filter((row) => row.period === selectedPeriod)
                  .sort((a, b) => (b.dummy_mcs || 0) - (a.dummy_mcs || 0))
                  .map((row) => {
                    const active =
                      String(row.plant_id) === String(selectedPlant);

                    return (
                      <tr
                        key={`${row.period}-${row.plant_id}`}
                        className={`border-b last:border-0 ${
                          active ? "bg-slate-50" : ""
                        }`}
                      >
                        <td className="px-3 py-3 font-semibold">
                          {row.plant}
                        </td>
                        <td className="px-3 py-3">{row.period}</td>
                        <td className="px-3 py-3 text-right font-semibold">
                          {fmt.format(row.dummy_mcs || 0)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPlant(String(row.plant_id));
                              setPage(1);
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            {active ? "Selected" : "Drill Down"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                {!loading &&
                !periodRows.some((row) => row.period === selectedPeriod) ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No dummy carton comparison data available.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedAggregate?.plant || "Plant"} ·{" "}
                {selectedPeriod || "Period"} Detail
              </h2>

              <p className="text-sm text-slate-500">
                {fmt.format(detailSummary.records || 0)} production records ·{" "}
                {fmt.format(detailSummary.dummy_mcs || 0)} Dummy MCS
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search date, plant, packing type..."
                className="w-72 rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                {[25, 50, 100, 250, 500].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={exportCsv}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3">Production Date</th>
                  <th className="px-3 py-3">Plant</th>
                  <th className="px-3 py-3">Packing Type</th>
                  <th className="px-3 py-3 text-right">Actual MCS</th>
                </tr>
              </thead>

              <tbody>
                {detailLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      Loading dummy carton detail…
                    </td>
                  </tr>
                ) : pagedRows.length ? (
                  pagedRows.map((row, index) => (
                    <tr
                      key={`${row.ProductionDate}-${row.PlantID}-${index}`}
                      className="border-b last:border-0"
                    >
                      <td className="px-3 py-3">
                        {row.ProductionDate || "—"}
                      </td>
                      <td className="px-3 py-3 font-medium">
                        {selectedAggregate?.plant ||
                          detailSummary.plant ||
                          `Plant ${row.PlantID}`}
                      </td>
                      <td className="px-3 py-3">
                        {row.PackingTypeID || "—"}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold">
                        {fmt.format(row.ActualMcs || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-slate-500"
                    >
                      No dummy carton detail available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t p-4 text-sm text-slate-600">
            <div>
              Showing{" "}
              {filteredRows.length
                ? (safePage - 1) * pageSize + 1
                : 0}
              –{Math.min(safePage * pageSize, filteredRows.length)} of{" "}
              {fmt.format(filteredRows.length)}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Previous
              </button>

              <span className="text-xs">
                Page {safePage} / {totalPages}
              </span>

              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPage((value) => Math.min(totalPages, value + 1))
                }
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Calculation & Validation
          </h2>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Calculation
              </div>
              <div className="mt-1 text-xs text-slate-700">
                SUM(ActualMcs) where UPPER(TRIM(PackingTypeID)) = DUMMY
              </div>
            </div>

            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Date Basis
              </div>
              <div className="mt-1 text-xs text-slate-700">
                ProductionDate
              </div>
            </div>

            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Source / Status
              </div>
              <div className="mt-1 text-xs text-slate-700">
                CsDailyProductionData ·{" "}
                {meta?.validation?.status || "unavailable"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
